import { api } from '../console/api.ts';
import { field, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, fmt, icon, type Money, person, when } from '../console/format.ts';
import { current, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { $, act } from '../console/ui.ts';
import { dateControl, selectControl, textareaControl } from '../controls.ts';

/**
 * What the policy's «Prima y recibos» tab shows of collections (#315): each receipt as the
 * carrier billed it (its number and series, the days it covers, its kind and its premium by
 * part), each payment with its voucher (comprobante) and its reversal, and the collections log
 * (calls, messages, visits and promises to pay). The printable voucher lives here too.
 */

export type ReceiptBreakdown = { net: Money; fees: Money; surcharge: Money; tax: Money };
export type ReceiptView = {
  number: number;
  dueDate: string;
  amount: Money;
  status: string;
  unconfirmed?: boolean;
  paidOn: string | null;
  endorsementRef: string | null;
  kind?: string;
  carrierRequirement?: { number: string; series: string } | null;
  coversFrom?: string | null;
  coversTo?: string | null;
  breakdown?: ReceiptBreakdown | null;
  voidReason?: string | null;
  partiallyPaid?: Money | null;
  paymentId?: string | null;
  remittanceId?: string | null;
};
export type PaymentView = {
  id: string;
  installment: number;
  amount: Money;
  method: string;
  reference: string;
  capturedOn: string;
  capturedBy: string;
  cashBoxId?: string | null;
  reversal?: { reason: string; reversedBy: string; reversedOn: string } | null;
};
export type EffortView = {
  id: string;
  installment: number;
  channel: string;
  outcome: string;
  note: string;
  promisedOn: string | null;
  promisedAmount: Money | null;
  standing: string | null;
  recordedBy: string;
  recordedOn: string;
  recordedAt: string;
};
export type Voucher = {
  paymentId: string;
  carrierName: string;
  policyNumber: string;
  policyholder: string | null;
  installment: number;
  installments: number;
  dueDate: string;
  coversFrom: string | null;
  coversTo: string | null;
  carrierRequirement: { number: string; series: string } | null;
  amount: Money;
  method: string;
  reference: string;
  cashBoxId: string | null;
  capturedOn: string;
  capturedBy: string;
  reversal: { reason: string; reversedBy: string; reversedOn: string } | null;
};

export const METHOD: Record<string, string> = {
  CASH: 'Efectivo',
  TRANSFER: 'Transferencia',
  CARD: 'Tarjeta',
  PAYMENT_LINK: 'Enlace de pago',
};
export const KIND: Record<string, string> = {
  FIRST: 'Primera cuota',
  INSTALLMENT: 'Fraccionado',
  ENDORSEMENT: 'Endoso',
  RENEWAL: 'Renovación',
};
export const VOID_REASON: Record<string, string> = {
  CUSTOMER_REQUEST: 'a solicitud del cliente',
  RISK_TRANSFERRED: 'el riesgo se transfirió',
  NON_PAYMENT: 'por falta de pago',
  POLICY_CANCELLED: 'por la cancelación de la póliza',
  CANCELLED_BY_CARRIER: 'lo anuló la aseguradora',
};
export const CHANNEL: Record<string, string> = {
  CALL: 'Llamada',
  WHATSAPP: 'WhatsApp',
  VISIT: 'Visita',
  EMAIL: 'Correo',
};
export const OUTCOME: Record<string, string> = {
  PROMISED: 'Promesa de pago',
  REACHED: 'Contactado, sin promesa',
  NO_ANSWER: 'No contestó',
  REFUSED: 'No va a pagar',
  WRONG_CONTACT: 'Dato de contacto equivocado',
};
export const STANDING: Record<string, [label: string, tone: string]> = {
  PENDING: ['Promesa vigente', 'info'],
  KEPT: ['Cumplida', 'ok'],
  BROKEN: ['Sin cumplir', 'bad'],
};

/** Who reverses a payment (Cerbos `reverse_payment`): the direction and the administration. */
export const reversesPayments = () => has('agency_director', 'agency_admin');
/** Who records collection efforts (`record_collection_effort`): collections and management. */
export const recordsEfforts = () => has('agency_director', 'agency_admin', 'collections_officer');

/** «R-2026-000123 · serie A»: the carrier's number of the receipt. */
export const requirementText = (r: ReceiptView['carrierRequirement']) =>
  r ? `${r.number} · serie ${r.series}` : null;

/** What a receipt says under its number, amount and state (#315). */
export function receiptDetails(i: ReceiptView) {
  const req = requirementText(i.carrierRequirement);
  return {
    number: req
      ? `<div class="muted small mono" data-requirement title="Requerimiento de la aseguradora">Req. ${esc(req)}</div>`
      : '',
    kind: i.kind
      ? `<div class="muted small" data-receipt-kind>${esc(KIND[i.kind] ?? i.kind)}</div>`
      : '',
    state: [
      i.voidReason && i.status === 'CANCELLED'
        ? `<div class="muted small" data-void-reason>${esc(VOID_REASON[i.voidReason] ?? i.voidReason)}</div>`
        : '',
      i.partiallyPaid
        ? `<div class="muted small">${fmt(i.partiallyPaid)} recibidos por la aseguradora</div>`
        : '',
    ].join(''),
    paid: i.remittanceId
      ? `<div class="receipt-note small" data-remitted>Remesado (${esc(i.remittanceId)})</div>`
      : i.paidOn && i.paymentId
        ? '<div class="receipt-note small" data-remitted>Por remesar</div>'
        : '',
    breakdown: i.breakdown
      ? `<details class="receipt-breakdown" data-receipt-breakdown><summary>Desglose</summary><dl class="breakdown-list">
          <dt>Prima neta</dt><dd class="num">${fmt(i.breakdown.net)}</dd>
          <dt>Gastos</dt><dd class="num">${fmt(i.breakdown.fees)}</dd>
          <dt>Recargo</dt><dd class="num">${fmt(i.breakdown.surcharge)}</dd>
          <dt>IVA</dt><dd class="num">${fmt(i.breakdown.tax)}</dd></dl></details>`
      : '',
  };
}

/** A payment's state and its actions (voucher; reverse for management, while not reversed). */
export function paymentCells(p: PaymentView, byAgency: boolean) {
  const reversed = p.reversal;
  const state = reversed
    ? `${pill('bad', 'Revertido')}<div class="muted small" data-reversal-reason>${esc(reversed.reason)} · ${esc(person(reversed.reversedBy))}, ${esc(date(reversed.reversedOn))}</div>`
    : pill('ok', 'Aplicado');
  const actions = [
    `<button type="button" class="btn sm" data-voucher="${esc(p.id)}">${icon('file')}Comprobante</button>`,
    !reversed && byAgency && reversesPayments()
      ? `<button type="button" class="btn sm danger" data-reverse-open="${esc(p.id)}" aria-expanded="false">Revertir</button>`
      : '',
  ].join(' ');
  const panel =
    !reversed && byAgency && reversesPayments()
      ? `<tr data-reverse-panel="${esc(p.id)}" hidden><td colspan="8"><div class="toolbar">
          <div class="grow"><strong>Revertir el pago de ${fmt(p.amount)}</strong><span class="muted small">El recibo vuelve a pendiente y el libro mayor registra el asiento contrario${p.method === 'CASH' ? '; el efectivo sale de la caja abierta' : ''}.</span></div>
          ${field('Motivo', textareaControl({ id: `reverse-reason-${p.id}`, rows: 2, maxlength: 300, placeholder: 'Transferencia rechazada por el banco', label: 'Motivo', hideLabel: true }), { required: true })}
          <button type="button" class="btn danger" data-reverse="${esc(p.id)}">Revertir pago</button></div></td></tr>`
      : '';
  return { state, actions, panel };
}

/** The collections log of the policy, and the form to record an effort (#315). */
export function effortsHtml(efforts: EffortView[], receipts: ReceiptView[], scheduleId: string) {
  const n = receipts.length;
  const owed = receipts
    .filter((i) => !['PAID', 'CANCELLED'].includes(i.status))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.number - b.number);
  const log = efforts.length
    ? `<ol class="timeline" data-efforts>${efforts
        .map((e) => {
          const [label, tone] = e.standing ? (STANDING[e.standing] ?? [e.standing, 'info']) : [];
          return `<li>${pill(e.outcome === 'PROMISED' ? 'info' : e.outcome === 'REFUSED' ? 'bad' : 'DRAFT', OUTCOME[e.outcome] ?? e.outcome)}<span class="muted small">${esc(when(e.recordedAt))} · ${esc(CHANNEL[e.channel] ?? e.channel)} · ${esc(person(e.recordedBy))}</span>
            <span class="timeline-note">Recibo ${e.installment}/${n}${e.promisedOn ? ` · pagará el <strong>${esc(date(e.promisedOn))}</strong>${e.promisedAmount ? ` ${fmt(e.promisedAmount)}` : ''} ${label && tone ? pill(tone, label) : ''}` : ''}${e.note ? `<br>${esc(e.note)}` : ''}</span></li>`;
        })
        .join('')}</ol>`
    : '<p class="muted policy-note">Sin gestiones de cobro registradas.</p>';
  const form =
    recordsEfforts() && owed.length
      ? `<div class="toolbar effort-form" data-effort-form>
          ${field(
            'Recibo',
            selectControl(
              { id: 'effort-receipt', label: 'Recibo', hideLabel: true },
              owed.map((i, k) => ({
                value: String(i.number),
                label: `${i.number}/${n} · ${fmt(i.amount)} · vence ${date(i.dueDate)}`,
                selected: k === 0,
              })),
            ),
          )}
          ${field(
            'Medio',
            selectControl(
              { id: 'effort-channel', label: 'Medio', hideLabel: true },
              Object.entries(CHANNEL).map(([value, label]) => ({ value, label })),
            ),
          )}
          ${field(
            'Resultado',
            selectControl(
              { id: 'effort-outcome', label: 'Resultado', hideLabel: true },
              Object.entries(OUTCOME).map(([value, label]) => ({ value, label })),
            ),
          )}
          ${field('Pagará el', dateControl({ id: 'effort-promise', label: 'Pagará el', hideLabel: true }))}
          ${field('Nota', textareaControl({ id: 'effort-note', rows: 2, maxlength: 500, placeholder: 'Qué dijo el cliente', label: 'Nota', hideLabel: true }))}
          <button type="button" class="btn primary" data-effort="${esc(scheduleId)}">Registrar gestión</button></div>`
      : '';
  return `<section class="card" data-component="efforts"><div class="card-head"><h3>Gestión de cobro</h3><span class="spacer"></span><span class="muted small">${efforts.length} ${efforts.length === 1 ? 'gestión' : 'gestiones'}</span></div>${form}${log}</section>`;
}

/** The voucher (comprobante de pago), printable: the print styles show only it. */
export function voucherHtml(v: Voucher, agency: string) {
  const req = requirementText(v.carrierRequirement);
  const rows: [string, string][] = [
    ['Póliza', `<span class="mono">${esc(v.policyNumber)}</span> · ${esc(v.carrierName)}`],
    ['Contratante', esc(v.policyholder ?? '—')],
    ['Recibo', `${v.installment} de ${v.installments}${req ? ` · requerimiento ${esc(req)}` : ''}`],
    [
      'Período que cubre',
      v.coversFrom && v.coversTo
        ? `${esc(date(v.coversFrom))} – ${esc(date(v.coversTo))}`
        : `vence el ${esc(date(v.dueDate))}`,
    ],
    ['Monto', `<strong>${fmt(v.amount)}</strong>`],
    ['Método', esc(METHOD[v.method] ?? v.method)],
    ['Referencia', `<span class="mono">${esc(v.reference)}</span>`],
    ['Fecha de pago', esc(date(v.capturedOn))],
    ['Registró', esc(person(v.capturedBy))],
  ];
  return `<section class="card voucher" data-voucher-card="${esc(v.paymentId)}" role="region" aria-labelledby="voucher-title">
    <div class="card-head"><h3 id="voucher-title">Comprobante de pago <span class="mono">${esc(v.paymentId)}</span></h3><span class="spacer"></span>${v.reversal ? pill('bad', 'Revertido') : pill('ok', 'Pagado')}</div>
    <p class="muted small">${esc(agency)}</p>
    <dl class="voucher-list">${rows.map(([k, x]) => `<dt>${esc(k)}</dt><dd>${x}</dd>`).join('')}</dl>
    ${v.reversal ? `<p class="note">${icon('alert')}<span>Este pago se revirtió el ${esc(date(v.reversal.reversedOn))}: ${esc(v.reversal.reason)}.</span></p>` : ''}
    <div class="toolbar no-print"><button type="button" class="btn primary" data-voucher-print>${icon('file')}Imprimir o guardar en PDF</button>
      ${v.reversal ? '' : `<button type="button" class="btn" data-voucher-send="${esc(v.paymentId)}">Enviar al cliente</button>`}
      <button type="button" class="btn" data-voucher-close>Cerrar</button></div></section>`;
}

/** The buttons of collections on the policy page (registered with the policies' view). */
export const cobranzaClicks: Record<string, (t: HTMLElement, d: DOMStringMap) => void> = {
  voucher: (t, d) => {
    const id = d.voucher;
    const slot = document.querySelector<HTMLElement>('[data-voucher-slot]');
    if (!id || !slot) return;
    void act(t, 'Preparando el comprobante…', async () => {
      const v = await api<Voucher>('GET', `/v1/payments/${encodeURIComponent(id)}/voucher`);
      slot.innerHTML = voucherHtml(v, document.querySelector('.brand-name')?.textContent ?? '');
      slot.hidden = false;
      slot.querySelector<HTMLElement>('[data-voucher-print]')?.focus();
      return `Comprobante del recibo ${v.installment} de ${v.installments} listo para imprimir.`;
    });
  },
  voucherPrint: () => {
    document.body.classList.add('printing-voucher');
    window.print();
    document.body.classList.remove('printing-voucher');
  },
  voucherClose: () => {
    const slot = document.querySelector<HTMLElement>('[data-voucher-slot]');
    if (slot) {
      slot.hidden = true;
      slot.innerHTML = '';
    }
  },
  voucherSend: (t, d) => {
    const id = d.voucherSend;
    if (!id) return;
    void act(t, 'Enviando el comprobante…', async () => {
      const r = await api<{ channel: string; status: string; simulated: boolean }>(
        'POST',
        `/v1/payments/${encodeURIComponent(id)}/voucher/send`,
        {},
      );
      const by = r.channel === 'EMAIL' ? 'correo' : 'WhatsApp';
      if (r.status === 'FAILED')
        throw new InputError(`No se pudo enviar el comprobante por ${by}. Intenta de nuevo.`);
      return `Comprobante enviado al cliente por ${by}${r.simulated ? ' (simulado)' : ''}.`;
    });
  },
  reverseOpen: (t, d) => {
    const id = d.reverseOpen;
    const panel = id
      ? document.querySelector<HTMLElement>(`[data-reverse-panel="${CSS.escape(id)}"]`)
      : null;
    if (!panel) return;
    panel.hidden = !panel.hidden;
    t.setAttribute('aria-expanded', String(!panel.hidden));
    if (!panel.hidden) $<HTMLElement>(`#reverse-reason-${CSS.escape(id ?? '')}`)?.focus();
  },
  reverse: (t, d) => {
    const id = d.reverse;
    if (!id) return;
    void act(t, 'Revirtiendo el pago…', async () => {
      const reason = (
        (document.getElementById(`reverse-reason-${id}`) as HTMLInputElement | null)?.value ?? ''
      ).trim();
      if (!reason) throw new InputError('Escribe el motivo de la reversión.');
      const r = await api<{ installment: number }>(
        'POST',
        `/v1/payments/${encodeURIComponent(id)}/reverse`,
        { reason },
      );
      await render(current, { quiet: true });
      return `Pago revertido: el recibo ${r.installment} vuelve a pendiente.`;
    });
  },
  effort: (t, d) => {
    const scheduleId = d.effort;
    if (!scheduleId) return;
    const value = (sel: string) =>
      ((document.querySelector(sel) as HTMLInputElement | null)?.value ?? '').trim();
    void act(t, 'Registrando la gestión…', async () => {
      const outcome = value('#effort-outcome');
      const promisedOn = value('#effort-promise');
      if (outcome === 'PROMISED' && !promisedOn)
        throw new InputError('Una promesa de pago necesita la fecha en que pagará.');
      if (outcome !== 'PROMISED' && promisedOn)
        throw new InputError('Solo una promesa de pago lleva fecha: elige «Promesa de pago».');
      await api('POST', `/v1/schedules/${encodeURIComponent(scheduleId)}/collection-efforts`, {
        installment: Number(value('#effort-receipt')),
        channel: value('#effort-channel'),
        outcome,
        note: value('#effort-note'),
        promisedOn: promisedOn || null,
      });
      await render(current, { quiet: true });
      return outcome === 'PROMISED'
        ? `Promesa de pago registrada para el ${date(promisedOn)}; te recordaremos si no se paga.`
        : 'Gestión de cobro registrada.';
    });
  },
};
